// --- React ---
import React, { useState, useEffect } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 공통 UI 컴포넌트 ---
import { ModalShell, FormStack, FormField, FormLabel, FormHelperText } from '@/components/ui';
import { Button, Input } from '@/components/ui';
import { PhoneInput, normalizePhone } from '@/components/ui/common/PhoneInput';

// --- 스토어 ---
import { useNotificationStore } from '../../../store/notificationStore';
import { useAppStore } from '../../../store/loadingStore';

// --- API ---
import {
  useCreateSiteManager,
  useUpdateSiteManager,
  type SiteManager,
} from '@/api/company/useSiteManagers';

// --- 유효성 검사 · 에러 파싱 ---
import { parseCompanyApiError } from '@/utils/errors';

// ==================== 타입 정의 ====================

interface SiteManagerCrudModalProps {
  isOpen: boolean;
  siteManager: SiteManager | null;
  companyId: number;
  onClose: () => void;
  onSave: () => void;
}

// ==================== 컴포넌트 ====================

const SiteManagerCrudModal: React.FC<SiteManagerCrudModalProps> = ({
  isOpen,
  siteManager,
  companyId,
  onClose,
  onSave,
}) => {
  // ==================== 다국어 ====================
  const { t } = useTranslation();

  // ==================== 스토어 ====================
  const { showSuccess, showError } = useNotificationStore();
  const { showLoading, hideLoading } = useAppStore();

  // ==================== 상태 관리 ====================
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    notes: '',
  });
  const [phoneValid, setPhoneValid] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // ==================== API 훅 ====================
  const createMutation = useCreateSiteManager(companyId);
  const updateMutation = useUpdateSiteManager(companyId);

  // ==================== 이펙트 ====================

  /**
   * 모달 열림 / 매니저 변경 시 폼 동기화
   * - 수정 모드: 기존 매니저 정보로 채움
   * - 생성 모드: 빈 폼 초기화
   */
  useEffect(() => {
    if (!isOpen) return;
    setPhoneValid(true);
    setErrors({});
    if (siteManager) {
      setFormData({
        email: siteManager.email ?? '',
        name: siteManager.name ?? '',
        phone: normalizePhone(siteManager.phone ?? ''),
        notes: '',
      });
    } else {
      setFormData({ email: '', name: '', phone: '', notes: '' });
    }
  }, [isOpen, siteManager]);

  // ==================== 유효성 검사 ====================

  /**
   * 생성 모드에서만 이메일 필수/형식 검사 수행
   * 수정 모드에서는 이메일 변경 불가
   */
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!siteManager) {
      if (!formData.email.trim()) {
        newErrors.email = 'company:site_manager_email_required';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          newErrors.email = 'company:site_manager_email_invalid';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== 핸들러 ====================

  /**
   * 저장 핸들러
   * 1. 유효성 검사
   * 2. 수정 모드 → updateMutation / 생성 모드 → createMutation 호출
   * 3. 성공/실패 알림
   */
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      showLoading();

      if (siteManager) {
        await updateMutation.mutateAsync({
          id: siteManager.id,
          name: formData.name.trim() || undefined,
          phone: formData.phone.trim() || undefined,
          notes: formData.notes.trim() || undefined,
        });
        showSuccess('company:site_manager_update_success');
      } else {
        await createMutation.mutateAsync({
          email: formData.email.trim(),
          name: formData.name.trim() || '',
          phone: formData.phone.trim() || undefined,
          notes: formData.notes.trim() || undefined,
        });
        showSuccess('company:site_manager_create_success');
      }

      onSave();
      onClose();
    } catch (error: unknown) {
      const e = parseCompanyApiError(error);
      showError(e.title, e.message);
    } finally {
      hideLoading();
    }
  };

  /** 모달 닫기 — 폼 데이터 초기화 후 부모 onClose 호출 */
  const handleClose = () => {
    setFormData({ email: '', name: '', phone: '', notes: '' });
    setPhoneValid(true);
    setErrors({});
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ==================== 렌더링 ====================

  return (
    <ModalShell
      isOpen={isOpen}
      title={
        siteManager
          ? t(i18nKeys.company.site_manager_edit)
          : t(i18nKeys.company.site_manager_add_new)
      }
      onClose={handleClose}
      size="md"
      footer={
        <>
          {/* 취소 버튼 */}
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            {t(i18nKeys.company.cancel)}
          </Button>
          {/* 저장 버튼 */}
          <Button onClick={handleSave} disabled={isPending || (!!formData.phone && !phoneValid)}>
            {isPending ? t(i18nKeys.company.processing) : t(i18nKeys.company.save)}
          </Button>
        </>
      }
    >
      <FormStack>
        {/* 이메일 — 생성 모드: 편집 가능, 수정 모드: 읽기 전용 */}
        {!siteManager && (
          <FormField>
            <FormLabel>{t(i18nKeys.company.site_manager_email)} *</FormLabel>
            <Input
              value={formData.email}
              onChange={value => {
                setFormData({ ...formData, email: value });
                setErrors({ ...errors, email: '' });
              }}
              placeholder={t(i18nKeys.company.site_manager_email_placeholder)}
              error={errors.email}
              size="md"
              fullWidth
            />
            <FormHelperText>{t(i18nKeys.company.site_manager_email_hint)}</FormHelperText>
          </FormField>
        )}

        {siteManager && (
          <FormField>
            <FormLabel>{t(i18nKeys.company.site_manager_email)}</FormLabel>
            <Input
              value={formData.email}
              onChange={() => {}}
              placeholder={t(i18nKeys.company.site_manager_email_placeholder)}
              disabled
              size="md"
              fullWidth
            />
            <FormHelperText>{t(i18nKeys.company.site_manager_email_readonly)}</FormHelperText>
          </FormField>
        )}

        {/* 이름 */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.site_manager_name)}</FormLabel>
          <Input
            value={formData.name}
            onChange={value => setFormData({ ...formData, name: value })}
            placeholder={t(i18nKeys.company.site_manager_name_placeholder)}
            size="md"
            fullWidth
          />
        </FormField>

        {/* 전화번호 */}
        <FormField>
          <PhoneInput
            label={t(i18nKeys.company.site_manager_phone)}
            value={formData.phone}
            onChange={(value, isValid) => {
              setFormData({ ...formData, phone: value });
              setPhoneValid(isValid);
            }}
            fullWidth
          />
        </FormField>

        {/* 메모 */}
        <FormField>
          <FormLabel>{t(i18nKeys.company.site_manager_notes)}</FormLabel>
          <Input
            value={formData.notes}
            onChange={value => setFormData({ ...formData, notes: value })}
            placeholder={t(i18nKeys.company.site_manager_notes_placeholder)}
            size="md"
            fullWidth
          />
        </FormField>
      </FormStack>
    </ModalShell>
  );
};

export default SiteManagerCrudModal;
